import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable, { HeadCell } from '../../components/tables/BakeasyTable';
import { BakeasyChip, BakeasyChipFilterCancel } from '../../components/common/BakeasyChip';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { fetchProducts, productsState } from '../../redux_store/slices/productsSlice';
import { FILTERS_ARRAY, UNAUTHORIZED } from '../../constants/Constants';
import { authActions } from '../../redux_store/slices/authSlice';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import * as BakeasyButton from '../../components/buttons/BakeasyButtons';
import { formatDateTime } from '../../utils/DateUtils';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { deleteKeyFromObject } from '../../utils/commonUtils';

export const Products: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);

  const [productsList, setProductsList] = useState<any[]>([]);
  const [count, setCount] = React.useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } = useAppSelector(productsState);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((product: any) => {
        return {
          id: product._id,
          productName: product?.productName,
          onProductNameClick: () => {
            navigate('/productDetails', { state: { isView: true, _id: product._id } });
          },
          supplierName: product?.supplierName,
          onSupplierNameClick: () => {
            navigate('/addEditSupplier', {
              state: { isView: true, isEdit: false, _id: product?.supplierId }
            });
          },
          contact: product?.contact1?.firstName + ' ' + product?.contact1?.lastName,
          onContactClick: () => {
            openContactProductConfirmDialog(product);
          },
          dateCreated: product?.createdDate ? formatDateTime(product?.createdDate) : '',
          lastAccess: product?.lastAccess ? formatDateTime(product?.createdDate) : '',
          status: <BakeasyChip label={product?.status} />,
          recipeBuilder: product.isReceipeBuilder ? (
            <BakeasyChip label="Yes" />
          ) : (
            <BakeasyChip label="No" />
          ),
          category: <BakeasyChip label={`${product.category}`} />,
          subCategory: <BakeasyChip label={`${product.subCategory}`} />
        };
      });

      setProductsList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload: any = { limit: rowsPerPage };

    if (search !== undefined && search !== '') {
      payload = { ...payload, search };
    }

    if (filter != undefined && filter !== '') {
      payload = { ...payload, ...filter };
    }

    payload = { ...payload, page: newPage + 1 };

    if (search === undefined || search === '') {
      delete payload['search'];
    }

    await dispatch(fetchProducts(payload));
  };

  const tableHeaders: HeadCell[] = [
    { id: 'productName', label: 'PRODUCT_NAME', sort: true },
    { id: 'supplierName', label: 'SUPPLIER_NAME', sort: true },
    { id: 'contact', label: 'SUPPLIER_CONTACT', sort: true },
    { id: 'dateCreated', label: 'DATE_CREATED' },
    { id: 'lastAccess', label: 'LAST_ACCESS' },
    { id: 'status', label: 'STATUS', align: 'center' },
    { id: 'recipeBuilder', label: 'RECIPE_BUILDER', isChip: true, align: 'center' }
  ];

  function openContactProductConfirmDialog(product: any) {
    myContext.setConfirmDialog({
      type: 'contact',
      open: true,
      title: 'Contact Supplier',
      data: product?.contact1
    });
  }

  function openFilterProductConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'product-filter',
      open: true,
      title: t('PRODUCT_FILTER'),
      data: filter,
      onNoClick: (event: any) => { },
      onYesClick: (event: any, param: string | undefined) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      }
    });
  }

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function productsUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('PRODUCTS')} />

          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div className="tableOptions">
                {/* <BakeasyButton.Export
                  onClick={function(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {}}
                /> */}
                <BakeasyButton.Filter
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openFilterProductConfirmDialog();
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <Grid sx={{ mt: 1, mb: -1 }}>
            {Object.entries(tFilter).map((item: any, index: number) => {
              if (!FILTERS_ARRAY.includes(item[0])) {
                return (
                  <BakeasyChipFilterCancel
                    key={index}
                    label={item[0] + ' : ' + item[1]}
                    onDelete={() => {
                      handleDelete(item[0]);
                    }}
                  />
                );
              }
            })}
          </Grid>

          <Grid>
            {productsList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={tableHeaders}
                tableData={productsList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return productsUI();
};
